import { useEffect, useState } from 'react';

import { Button } from 'antd';

import dayjs from 'dayjs';

import { sortDates, sortStrings } from '../../helpers/filter';

import { DATE_FORMAT } from '../../constants/common';
import { EVENT_MODAL_MODE } from '../../constants/event';
import { TABLE_CONFIG_COLUMNS, TABLE_CONFIG_KEYS } from '../../constants/table-configurations';

import AppTable from '../../components/AppTable';
import { COLUMN_SIZE } from '../../components/AppTable/constants';
import EventModal from '../../components/Planner/EventModal';

function CustomAgenda({ events }) {
  const [isOpenEventModal, setOpenEventModal] = useState(false);
  const [assignedUserFilterOptions, setAssignedUserFilterOptions] = useState([]);
  const [filteredInfo, setFilteredInfo] = useState({});

  useEffect(() => {
    setAssignedUserFilterOptions(composeAssignedUserFilterOptions());
  }, [events]);

  const composeAssignedUserFilterOptions = () => {
    const userEmails = new Set();

    const options = [];

    for (const event of events) {
      for (const user of event.users || []) {
        if (userEmails.has(user.email)) {
          continue;
        }

        options.push({ text: user.email, data: user.email, value: user.id });
        userEmails.add(user.email);
      }
    }

    return options;
  };

  const onAddEventClick = () => {
    setOpenEventModal(true);
  };

  const onSubmit = () => {
    setOpenEventModal(false);
  };

  const commonColumnProperties = {
    ellipsis: true,
    sortDirections: ['ascend', 'descend']
  };

  const setFilters = (_, filters) => {
    setFilteredInfo(filters);
  };

  const actions = [
    {
      label: (
        <Button color="default" size="small" variant="link" onClick={onAddEventClick}>
          Add event
        </Button>
      )
    }
  ];

  const columns = [
    {
      ...commonColumnProperties,
      title: 'Event',
      dataIndex: 'title',
      searchable: true,
      configColumnKey: TABLE_CONFIG_COLUMNS.TITLE,
      width: COLUMN_SIZE.XL,
      sorter: (a, b) => sortStrings(a.title, b.title)
    },
    {
      ...commonColumnProperties,
      title: 'Date',
      dataIndex: 'start_at',
      width: COLUMN_SIZE.LG,
      configColumnKey: TABLE_CONFIG_COLUMNS.START_AT,
      render: (_, record) => dayjs(record.start_at).format(DATE_FORMAT.DATE),
      sorter: (a, b) => sortDates(a.start_at, b.start_at)
    },
    {
      ...commonColumnProperties,
      title: 'Time',
      dataIndex: 'time',
      configColumnKey: TABLE_CONFIG_COLUMNS.TIME,
      width: COLUMN_SIZE.LG,
      render: (_, record) => {
        const start = dayjs(record.start).format(DATE_FORMAT.TIME);
        const end = dayjs(record.end).format(DATE_FORMAT.TIME);

        return `${start} - ${end}`;
      },
      sorter: (a, b) => sortDates(a.start, b.start)
    },
    {
      ...commonColumnProperties,
      title: 'Description',
      dataIndex: 'description',
      configColumnKey: TABLE_CONFIG_COLUMNS.DESCRIPTION,
      searchable: true,
      width: COLUMN_SIZE.XL,
      sorter: (a, b) => sortStrings(a.description, b.description)
    },
    {
      title: 'Assigned Users',
      searchable: true,
      dataIndex: 'assignedUser',
      filters: assignedUserFilterOptions,
      configColumnKey: TABLE_CONFIG_COLUMNS.ASSIGNED_USERS,
      width: COLUMN_SIZE.LG,
      onFilter: (value, record) => (record.users || []).some(user => user.id === value),
      render: (_, record) =>
        (record.users || []).map(user =>
          user.status || record.users.length === 1 ? (
            <span className="px-1" key={user.id}>
              {user.email}
            </span>
          ) : null
        )
    }
  ];
  return (
    <>
      <AppTable
        actions={actions}
        baseColumns={columns}
        headerClass="pb-2"
        dataSource={events}
        tableConfigKey={TABLE_CONFIG_KEYS.EVENTS}
        loading={false}
        rowsSelectable={false}
        searchable={false}
        settings={true}
        handleOnChange={setFilters}
      />

      <EventModal
        onSubmit={onSubmit}
        isOpen={isOpenEventModal}
        onCancel={() => setOpenEventModal(false)}
        mode={EVENT_MODAL_MODE.CREATE}
      />
    </>
  );
}

export default CustomAgenda;
